// Lightweight guard to avoid infinite reload loops when stale chunks keep failing.
const RELOAD_GUARD_KEY = '__stale_reload_in_progress__'
const RELOAD_ATTEMPT_LIMIT = 2
const RELOAD_GUARD_TTL_MS = 5 * 60 * 1000
const STALE_MODULE_SNIPPETS = [
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Failed to load module script',
    'Unable to preload CSS for',
    'CSS chunk load failed',
    'Loading chunk',
]

const STALE_ERROR_NAMES = new Set(['ChunkLoadError', 'CSS_CHUNK_LOAD_FAILED'])

const STALE_ERROR_CODES = new Set(['MODULE_NOT_FOUND', 'CSS_CHUNK_LOAD_FAILED'])

export function isStaleModuleImportError(error) {
    if (!error) return false

    if (typeof error?.name === 'string' && STALE_ERROR_NAMES.has(error.name)) {
        return true
    }

    if (typeof error?.code === 'string' && STALE_ERROR_CODES.has(error.code)) {
        return true
    }

    const message =
        typeof error?.message === 'string'
            ? error.message
            : typeof error === 'string'
              ? error
              : ''

    if (!message) {
        return false
    }

    return STALE_MODULE_SNIPPETS.some((snippet) => message.includes(snippet))
}

export async function forceReloadDueToStaleAssets(options = {}) {
    if (typeof window === 'undefined') {
        return
    }

    const now = Date.now()
    const {
        reason = 'stale-assets',
        serverVersion,
        versionStorageKey,
    } = options

    try {
        const rawGuard = window.sessionStorage?.getItem(RELOAD_GUARD_KEY)
        let guard = null

        if (rawGuard) {
            try {
                const parsed = JSON.parse(rawGuard)
                if (parsed && typeof parsed === 'object') {
                    guard = {
                        reason:
                            typeof parsed.reason === 'string'
                                ? parsed.reason
                                : '',
                        attempts:
                            typeof parsed.attempts === 'number'
                                ? parsed.attempts
                                : 1,
                        timestamp:
                            typeof parsed.timestamp === 'number'
                                ? parsed.timestamp
                                : 0,
                    }
                }
            } catch (_) {
                guard = {
                    reason: typeof rawGuard === 'string' ? rawGuard : '',
                    attempts: 1,
                    timestamp: 0,
                }
            }
        }

        const guardFresh =
            guard &&
            guard.timestamp &&
            now - guard.timestamp <= RELOAD_GUARD_TTL_MS

        if (guardFresh && guard.attempts >= RELOAD_ATTEMPT_LIMIT) {
            try {
                console.warn(
                    'Skipping forced reload due to guard limits:',
                    guard.reason || reason,
                    guard.attempts
                )
            } catch (_) {}
            return
        }

        // When the guard is stale, reset it so a new failure can try again.
        if (guard && !guardFresh) {
            try {
                window.sessionStorage?.removeItem(RELOAD_GUARD_KEY)
            } catch (_) {}
        }

        const nextGuard = {
            reason,
            attempts: guardFresh ? guard.attempts + 1 : 1,
            timestamp: now,
        }
        window.sessionStorage?.setItem(
            RELOAD_GUARD_KEY,
            JSON.stringify(nextGuard)
        )
    } catch (_) {}

    if (versionStorageKey && serverVersion) {
        try {
            window.localStorage?.setItem(versionStorageKey, serverVersion)
        } catch (_) {}
    }

    try {
        if (typeof caches !== 'undefined') {
            const keys = await caches.keys()
            await Promise.all(
                keys.map((key) => caches.delete(key).catch(() => {}))
            )
        }
    } catch (error) {
        console.warn('Failed to clear caches before reload', error)
    }

    try {
        if ('serviceWorker' in navigator) {
            const registrations =
                await navigator.serviceWorker.getRegistrations()
            await Promise.all(
                registrations.map((registration) =>
                    registration.unregister().catch(() => {})
                )
            )
        }
    } catch (error) {
        console.warn(
            'Failed to unregister service workers before reload',
            error
        )
    }

    window.location.replace(window.location.href)
}
